import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { playersApi, absUrl } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import { ArrowLeft, MapPin, Calendar, Phone, Mail, MessageCircle, CheckCircle2, XCircle, Trophy, Play, Loader2, Pencil } from 'lucide-react';

export default function PlayerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    setLoading(true);
    playersApi.get(id).then(setPlayer).catch(() => setPlayer(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="py-20 flex justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  if (!player) return (
    <div className="max-w-3xl mx-auto p-8 text-center">
      <p className="text-gray-500">Player not found.</p>
      <Link to="/discover"><Button variant="outline" className="mt-4">Back to discover</Button></Link>
    </div>
  );

  const contact = player.contact || {};

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mft-fade-in">
      <Link to="/discover" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-[color:var(--mft-green)] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to discover
      </Link>

      {user?.player_id === player.id && (
        <div className="mb-4 flex justify-end">
          <Link to="/my-profile">
            <Button className="mft-btn-primary gap-2">
              <Pencil className="w-4 h-4" /> Edit my profile
            </Button>
          </Link>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="h-32 bg-gradient-to-r from-[color:var(--mft-green)] to-[color:var(--mft-green-light)]" />
        <div className="px-6 md:px-8 pb-6 -mt-16">
          <div className="flex flex-col md:flex-row md:items-end gap-5">
            <div className="w-32 h-32 rounded-2xl border-4 border-white shadow-lg bg-gray-100 overflow-hidden">
              {player.profile_picture ? (
                <img src={absUrl(player.profile_picture)} alt={player.full_name} className="w-full h-full object-cover" />
              ) : null}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-3xl font-bold text-gray-900">{player.full_name}</h1>
              </div>
              {player.nickname && <p className="text-gray-500 italic">"{player.nickname}"</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge className="bg-[color:var(--mft-green)] text-white hover:bg-[color:var(--mft-green-dark)]">{player.position}</Badge>
                <span className="inline-flex items-center gap-1 text-sm text-gray-600"><Calendar className="w-4 h-4" /> {player.age} years</span>
                <span className="inline-flex items-center gap-1 text-sm text-gray-600"><MapPin className="w-4 h-4" /> {player.province}</span>
              </div>
            </div>
            <div>
              {player.available_for_offers ? (
                <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 font-semibold px-3 py-2 rounded-lg text-sm"><CheckCircle2 className="w-4 h-4" /> Available for offers</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 bg-gray-200 text-gray-700 font-semibold px-3 py-2 rounded-lg text-sm"><XCircle className="w-4 h-4" /> Not available</span>
              )}
            </div>
          </div>
          {player.bio && <p className="mt-5 text-gray-700 leading-relaxed max-w-3xl">{player.bio}</p>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Trophy className="w-5 h-5 text-[color:var(--mft-green)]" /> Career Journey</h2>
            <ol className="mt-4 relative border-l-2 border-[color:var(--mft-green)]/20 pl-5 space-y-4">
              {(player.career_journey || []).map((c, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-[color:var(--mft-green)]" />
                  <div className="font-semibold text-gray-900">{c.team}</div>
                  <div className="text-sm text-gray-500">{c.years} • {c.role}</div>
                </li>
              ))}
            </ol>
          </section>

          {player.pictures?.length > 0 && (
            <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Match Photos</h2>
                <span className="text-xs text-gray-500">{player.pictures.length} photo{player.pictures.length !== 1 ? 's' : ''}</span>
              </div>
              <Carousel opts={{ align: 'start', loop: player.pictures.length > 1 }} className="w-full">
                <CarouselContent className="-ml-3">
                  {player.pictures.map((src, i) => (
                    <CarouselItem key={i} className="pl-3 md:basis-1/2">
                      <div className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
                        <img src={absUrl(src)} alt={`Match photo ${i + 1}`} className="w-full h-full object-cover" />
                      </div>
                      <div className="mt-2 text-xs text-gray-500 text-center">Photo {i + 1} of {player.pictures.length}</div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {player.pictures.length > 1 && (
                  <>
                    <CarouselPrevious className="hidden sm:flex" />
                    <CarouselNext className="hidden sm:flex" />
                  </>
                )}
              </Carousel>
            </section>
          )}

          {player.videos?.length > 0 && (
            <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Video Clips</h2>
                <span className="text-xs text-gray-500">{player.videos.length} clip{player.videos.length !== 1 ? 's' : ''}</span>
              </div>
              <Carousel opts={{ align: 'start', loop: player.videos.length > 1 }} className="w-full">
                <CarouselContent className="-ml-3">
                  {player.videos.map((v, i) => (
                    <CarouselItem key={i} className="pl-3 md:basis-1/2">
                      <button
                        onClick={() => setActiveVideo(v)}
                        className="group w-full text-left bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:border-[color:var(--mft-green)] transition"
                      >
                        <div className="aspect-video bg-gray-900 relative flex items-center justify-center">
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="relative w-14 h-14 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow-lg">
                            <Play className="w-6 h-6 text-[color:var(--mft-green)] fill-[color:var(--mft-green)] ml-1" />
                          </div>
                          {v.duration && <span className="absolute bottom-2 right-2 text-white text-xs bg-black/60 px-2 py-0.5 rounded">{v.duration}</span>}
                        </div>
                        <div className="p-3">
                          <div className="font-medium text-sm text-gray-900 truncate">{v.title || `Clip ${i + 1}`}</div>
                          <div className="text-xs text-gray-500 mt-0.5">Clip {i + 1} of {player.videos.length}</div>
                        </div>
                      </button>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {player.videos.length > 1 && (
                  <>
                    <CarouselPrevious className="hidden sm:flex" />
                    <CarouselNext className="hidden sm:flex" />
                  </>
                )}
              </Carousel>
            </section>
          )}
        </div>

        <div className="space-y-6">
          <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm sticky top-20">
            <h2 className="text-lg font-bold text-gray-900">Contact</h2>
            <p className="text-sm text-gray-500 mt-1">Reach out directly to the player.</p>
            <div className="mt-5 space-y-3">
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition">
                  <div className="w-9 h-9 rounded-lg bg-[color:var(--mft-green)]/10 flex items-center justify-center"><Phone className="w-4 h-4 text-[color:var(--mft-green)]" /></div>
                  <div className="text-sm"><div className="text-gray-500 text-xs">Phone</div><div className="font-medium text-gray-900">{contact.phone}</div></div>
                </a>
              )}
              {contact.whatsapp && (
                <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition">
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center"><MessageCircle className="w-4 h-4 text-emerald-600" /></div>
                  <div className="text-sm"><div className="text-gray-500 text-xs">WhatsApp</div><div className="font-medium text-gray-900">{contact.whatsapp}</div></div>
                </a>
              )}
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition">
                  <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center"><Mail className="w-4 h-4 text-blue-600" /></div>
                  <div className="text-sm"><div className="text-gray-500 text-xs">Email</div><div className="font-medium text-gray-900 break-all">{contact.email}</div></div>
                </a>
              )}
            </div>
          </section>
        </div>
      </div>

      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setActiveVideo(null)}>
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-video rounded-xl overflow-hidden bg-black">
              {activeVideo.url.includes('youtube') ? (
                <iframe title={activeVideo.title} src={activeVideo.url} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              ) : (
                <video src={absUrl(activeVideo.url)} controls autoPlay className="w-full h-full" />
              )}
            </div>
            <div className="mt-3 flex justify-between items-center">
              <div className="text-white font-medium">{activeVideo.title}</div>
              <Button variant="secondary" onClick={() => setActiveVideo(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
